// components/Form2307RecordsTable.jsx
//
// A compact, read-only table of Form 2307 certificates for embedding in a
// module's Records section — currently VAT Management and Percentage Tax, which
// both want their withheld certificates visible in one go alongside the 2306s.
//
// Read-only on purpose. Every row here was derived automatically from posting a
// transaction that carried an ATC, and an auto-generated certificate is
// immutable — the backend refuses an in-place edit. Corrections and reissues go
// through the Form 2307 obligation screen's Correction / Reissue tab.
//
// The richer per-period view (totals, quarterly batch download per counterparty)
// lives in Form2307CertificatesPanel; this is the embedded summary table.
import React, { useState } from 'react';
import form2307API from '../api/form2307API';
import Button from './Button';
import notify from '../lib/notify';

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const quarterOf = (record) =>
  Number(record?.quarter) || (Number(record?.month) ? Math.ceil(Number(record.month) / 3) : null);

/** Integer centavos preferred; falls back to the peso column on older rows. */
const money = (centavos, pesoFallback) => {
  const value = centavos === null || centavos === undefined
    ? Math.round((Number(pesoFallback) || 0) * 100)
    : Number(centavos) || 0;
  return (value / 100).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const rate = (basisPoints, percentFallback) => {
  const bp = basisPoints === null || basisPoints === undefined
    ? Math.round((Number(percentFallback) || 0) * 100)
    : Number(basisPoints) || 0;
  return `${(bp / 100).toFixed(2)}%`;
};

const taxCentavosOf = (r) =>
  r.taxWithheldCentavos === null || r.taxWithheldCentavos === undefined
    ? Math.round((Number(r.taxWithheld) || 0) * 100)
    : Number(r.taxWithheldCentavos) || 0;

const Form2307RecordsTable = ({
  clientId,
  records = [],
  loading = false,
  title = 'Form 2307 (creditable tax withheld)',
  subtitle = 'Generated automatically when a transaction with an ATC is posted.',
  emptyHint = 'No 2307 records for the selected period.',
  footnote = null,
}) => {
  const [downloading, setDownloading] = useState(null);

  const totalCentavos = records
    .filter((r) => !r.isVoided)
    .reduce((sum, r) => sum + taxCentavosOf(r), 0);

  const handleDownload = async (cert) => {
    try {
      setDownloading(cert._id);
      await form2307API.downloadPdf(
        clientId,
        cert._id,
        `form-2307-${cert.certificateNumber || cert._id}.pdf`
      );
    } catch (e) {
      notify.error(e.message);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="border border-ink-line rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-ink-line bg-ink-canvas flex items-start justify-between gap-3">
        <div>
          <p className="font-medium text-ink">{title}</p>
          <p className="text-xs text-ink-body">{subtitle}</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-ink-body">Total withheld</div>
          <div className="text-sm font-medium text-ink tabular-nums">₱{money(totalCentavos)}</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-white">
            <tr className="text-left text-ink-body border-b border-ink-line">
              <th className="px-4 py-2">Certificate date</th>
              <th className="px-4 py-2">Period</th>
              <th className="px-4 py-2">Certificate no.</th>
              <th className="px-4 py-2">Counterparty</th>
              <th className="px-4 py-2">ATC</th>
              <th className="px-4 py-2 text-right">Base</th>
              <th className="px-4 py-2 text-right">Rate</th>
              <th className="px-4 py-2 text-right">Withheld</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="px-4 py-4 text-ink-body" colSpan={9}>Loading…</td>
              </tr>
            )}

            {!loading && records.length === 0 && (
              <tr>
                <td className="px-4 py-4 text-ink-body" colSpan={9}>{emptyHint}</td>
              </tr>
            )}

            {!loading && records.map((r) => (
              <tr
                key={r._id}
                className={`border-b border-ink-line last:border-b-0 ${r.isVoided ? 'opacity-60' : ''}`}
              >
                <td className="px-4 py-2 whitespace-nowrap">
                  {/*
                    certificateDate, not transactionDate: the certificate is
                    dated the last day of the month it is filed for, while the
                    transaction date is when the record happened to be encoded.
                  */}
                  {r.certificateDate ? new Date(r.certificateDate).toLocaleDateString('en-PH') : '—'}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <div className="text-ink">{quarterOf(r) ? `Q${quarterOf(r)}` : '—'}</div>
                  <div className="text-xs text-ink-body">
                    {Number(r.month) ? MONTH_LABELS[Number(r.month) - 1] : '—'} {r.fiscalYear || ''}
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className="tabular-nums">{r.certificateNumber || '—'}</span>
                  {r.isVoided && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-md bg-danger-50 text-danger-700">
                      Voided
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {(typeof r.vendor === 'object' && r.vendor?.name) || r.payeeName || '—'}
                  {typeof r.vendor === 'object' && r.vendor?.businessType === 'government' && (
                    <span className="block text-xs text-ink-body">Government</span>
                  )}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">{r.atcCode || '—'}</td>
                <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">
                  {money(r.baseAmountCentavos, r.baseAmount)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">
                  {rate(r.rateBasisPoints, r.withholdingRate)}
                </td>
                <td className="px-4 py-2 text-right font-medium text-ink tabular-nums whitespace-nowrap">
                  {money(r.taxWithheldCentavos, r.taxWithheld)}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(r)}
                    loading={downloading === r._id}
                    disabled={!!downloading || !clientId}
                  >
                    {r.isVoided ? 'Reprint' : 'Download'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {footnote && (
        <div className="px-4 py-3 bg-white text-xs text-ink-body">{footnote}</div>
      )}
    </div>
  );
};

export default Form2307RecordsTable;
